// Token API for managing external access tokens against backend
import { logAPI } from '../utils/logger';
import { TOKEN_EXPIRATION } from '../constants/appConstants';
import { API_BASE_URL } from '../config/apiConfig';

export interface TokenValidationResult {
  valid: boolean;
  tokenData?: {
    id: string;
    coordinatorEmail: string;
    expiresAt: string;
    isUsed: boolean;
    usedAt?: string;
    createdAt: string;
    // Set when the token was minted via create-draft-with-token / attach-to-draft.
    // The external form uses this to fetch the linked draft directly, avoiding
    // the now-authenticated GET /api/caseRequests list endpoint.
    caseRequestId?: number;
  };
  error?: string;
}

export interface CreateTokenRequest {
  coordinatorEmail: string;
  expiresIn?: number; // hours
}

export interface TokenCreationResult {
  token: string;
  expiresAt: string;
  coordinatorEmail: string;
}

// Validate a token
export const validateToken = async (token: string): Promise<TokenValidationResult> => {
  try {
  const response = await fetch(`${API_BASE_URL}/tokens/${token}`);
    
    if (!response.ok) {
      if (response.status === 404) {
        return { valid: false, error: 'Token not found' };
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
  const tokenData = await response.json();
    // Normalize date strings to ensure UTC parsing if backend omits timezone
    const normalizeUtc = (s?: string) => {
      if (!s) return s;
      return /Z$|[+-]\d{2}:\d{2}$/.test(s) ? s : `${s}Z`;
    };
    tokenData.createdAt = normalizeUtc(tokenData.createdAt);
    tokenData.expiresAt = normalizeUtc(tokenData.expiresAt);
    
    // Check if token is expired
  const now = new Date();
  const expiresAt = new Date(tokenData.expiresAt);
    if (now > expiresAt) {
      return { valid: false, error: 'Token has expired' };
    }
    
    // Check if token is already used
  if (tokenData.isUsed) {
      return { valid: false, error: 'Token has already been used' };
    }
    
    return { valid: true, tokenData };
  } catch (error) {
    logAPI.error('Token validation error:', error);
    return { valid: false, error: 'Failed to validate token' };
  }
};

// Create a new token
export const createToken = async (request: CreateTokenRequest): Promise<TokenCreationResult> => {
  try {
    const expiresIn = request.expiresIn || TOKEN_EXPIRATION.ONE_DAY;
    const response = await fetch(`${API_BASE_URL}/tokens`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ coordinatorEmail: request.coordinatorEmail, expiresInHours: expiresIn }),
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
  const created = await response.json();
  const data = created.tokenData || created; // controller returns { token, tokenData }
  return { token: created.token || data.id, expiresAt: data.expiresAt, coordinatorEmail: data.coordinatorEmail };
  } catch (error) {
    logAPI.error('Token creation error:', error);
    throw new Error('Failed to create token');
  }
};

// Mark a token as used
export const markTokenAsUsed = async (token: string): Promise<void> => {
  try {
  const response = await fetch(`${API_BASE_URL}/tokens/${token}/use`, { method: 'PATCH' });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
  } catch (error) {
    logAPI.error('Token update error:', error);
    throw new Error('Failed to mark token as used');
  }
}

export async function createDraftWithToken(coordinatorEmail?: string, expiresInHours = 72): Promise<{ caseRequestId: number; token: string; tokenData: any; }> {
  const res = await fetch(`${API_BASE_URL}/tokens/create-draft-with-token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ coordinatorEmail, expiresInHours })
  });
  if (!res.ok) throw new Error(`Failed to create draft with token: ${res.status}`);
  return res.json();
}

export async function attachTokenToDraft(caseRequestId: number, coordinatorEmail?: string, expiresInHours = 72): Promise<{ caseRequestId: number; token: string; tokenData: any; }> {
  const res = await fetch(`${API_BASE_URL}/tokens/attach-to-draft`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ caseRequestId, coordinatorEmail, expiresInHours })
  });
  if (!res.ok) throw new Error(`Failed to attach token to draft: ${res.status}`);
  return res.json();
}


// Backend generates token IDs; client helper removed

// Get all tokens (admin only)
export const getAllTokens = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/tokens`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    logAPI.error('Get tokens error:', error);
    throw new Error('Failed to retrieve tokens');
  }
};
